import { InvoiceRendererData } from '@sequencehq/invoice-content'
import { useQuery } from '@sequencehq/api'
import type { CountriesAlpha2 } from '@sequencehq/api/commonEnums'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

import { useAggregatedInvoice } from 'Invoices/hooks/useAggregatedInvoice'
import { toInvoiceRendererData } from 'Invoices/components/CreateEditShared/InvoiceRenderer/useInvoiceRendererData'
import { AggregatedInvoice } from 'Invoices/types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { InvoiceReviewFormValues } from '../types'

/**
 * Builds the `InvoiceRendererData` for the review modal's live document preview.
 * Mirrors `useInvoiceRendererData` (the invoice editor's preview), but sources
 * the invoice from `useAggregatedInvoice(invoiceId)` and overlays the operator's
 * in-progress edits — so the rendered "To" address, PO number, reference and memo
 * update live as the form is edited, exactly as they do in the invoice editor.
 *
 * The edits are applied to a local copy only; nothing is persisted here. The
 * confirmed values are applied by the backend when the manual task completes.
 */
export const useInvoiceReviewRendererData = ({
  invoiceId,
  editedValues
}: {
  invoiceId: string
  editedValues: InvoiceReviewFormValues
}): {
  rendererData: InvoiceRendererData | undefined
  isPending: boolean
  isError: boolean
} => {
  const { invoice, isLoading, invoiceNotFound, invoiceIsError } =
    useAggregatedInvoice(invoiceId)
  const { showServicePeriodsAlways, usePdfInvoiceAlternativeHeader } =
    useFeatureFlags()

  const editedInvoice = applyEditedValues(invoice, editedValues)

  const { data: merchantData } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const { data: accountSettingsData } = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  // Keyed on the edited billing address, so tax details re-resolve when the
  // operator changes the invoice's country/state — matching the editor. An
  // invoice whose customer has no address has no country to key on, so it falls
  // back to the merchant's own: the preview should still render while the
  // operator fills the address in.
  const merchantDetailsCountry =
    editedInvoice?.customerBillingAddress?.country ??
    merchantData?.address.country

  const { data: invoiceMerchantData } = useQuery(
    dashboard20240730Client.getInvoiceMerchantDetails,
    {
      currency: editedInvoice?.currency ?? 'USD',
      country: merchantDetailsCountry as CountriesAlpha2,
      state: editedInvoice?.customerBillingAddress?.state
    },
    { enabled: Boolean(merchantDetailsCountry) }
  )

  const { data: currencyConversions } = useQuery(
    dashboard20240730Client.getInvoiceCurrencyConversions,
    { id: invoiceId }
  )

  const { data: dueDateSettings } = useQuery(
    dashboardv20240509Client.getInvoiceAccountingDateSettings
  )

  // The invoice is the critical source; if it can't be loaded there's nothing to
  // render, so surface an error the preview can show instead of an endless
  // skeleton. The supporting merchant/settings queries only gate the pending
  // state.
  const isError = invoiceNotFound || invoiceIsError
  const isPending =
    isLoading ||
    !merchantData ||
    !accountSettingsData ||
    !invoiceMerchantData ||
    !dueDateSettings

  if (isError) {
    return { rendererData: undefined, isPending: false, isError: true }
  }

  if (!editedInvoice || isPending) {
    return { rendererData: undefined, isPending, isError: false }
  }

  return {
    rendererData: toInvoiceRendererData({
      clientInvoice: editedInvoice,
      defaultCurrency: accountSettingsData.enabledCurrencies.at(0),
      merchantData,
      invoiceMerchantData,
      currencyConversions: currencyConversions?.items ?? [],
      invoiceDueDateCalculatedFrom:
        dueDateSettings.invoiceDueDateCalculatedFrom,
      alwaysShowServicePeriod: showServicePeriodsAlways,
      usePdfInvoiceAlternativeHeader,
      buildCreditNoteUrl: creditNoteId =>
        `${window.location.origin}/credit-notes/${creditNoteId}`
    }),
    isPending: false,
    isError: false
  }
}

/**
 * Overlays the operator's edited fields onto the fetched invoice. Free-text
 * fields become `undefined` when blank so the document renders them empty rather
 * than as a blank string.
 */
const applyEditedValues = (
  invoice: AggregatedInvoice | null,
  editedValues: InvoiceReviewFormValues
): AggregatedInvoice | null => {
  if (!invoice) {
    return null
  }

  return {
    ...invoice,
    customerBillingAddress: editedValues.customerBillingAddress,
    purchaseOrderNumber: blankToUndefined(editedValues.purchaseOrderNumber),
    reference: blankToUndefined(editedValues.reference),
    memo: blankToUndefined(editedValues.memo)
  }
}

const blankToUndefined = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value
